import { normalizeLicensePlate } from '@/utils/licensePlate'

const PROVINCES = ['GP', 'WC', 'EC', 'KZN', 'FS', 'LP', 'MP', 'NW', 'NC']

/** SA plate patterns — ordered most specific first. */
const PLATE_PATTERNS = [
  { id: 'legacy_3L3D2L', re: /^[A-Z]{3}\d{3}(GP|WC|EC|KZN|FS|LP|MP|NW|NC)$/, weight: 100 },
  { id: 'legacy_2L3D2L', re: /^[A-Z]{2}\d{3}(GP|WC|EC|KZN|FS|LP|MP|NW|NC)$/, weight: 95 },
  { id: 'modern_2L6D', re: /^[A-Z]{2}\d{6}$/, weight: 90 },
  { id: 'mixed_letters_digits', re: /^[A-Z]{2,4}\d{2,6}[A-Z]{0,3}$/, weight: 70 },
  { id: 'general_alnum', re: /^[A-Z0-9]{5,9}$/, weight: 45 },
]

const CONFUSION_PAIRS = [
  ['O', '0'],
  ['0', 'O'],
  ['I', '1'],
  ['1', 'I'],
  ['L', '1'],
  ['1', 'L'],
  ['B', '8'],
  ['8', 'B'],
  ['S', '5'],
  ['5', 'S'],
  ['Z', '2'],
  ['2', 'Z'],
  ['G', '6'],
  ['6', 'G'],
  ['D', '0'],
  ['0', 'D'],
  ['Q', '0'],
  ['0', 'Q'],
]

function fixOcrText(text) {
  return String(text || '')
    .toUpperCase()
    .replace(/[|\\]/g, 'I')
    .replace(/[`'"]/g, '')
    .replace(/[^A-Z0-9\s\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function patternScore(norm) {
  for (const p of PLATE_PATTERNS) {
    if (p.re.test(norm)) return p.weight
  }
  return 0
}

function generateConfusionVariants(norm, maxVariants = 24) {
  const variants = new Set([norm])
  const chars = norm.split('')

  function recurse(idx, current) {
    if (variants.size >= maxVariants) return
    if (idx >= chars.length) {
      variants.add(current.join(''))
      return
    }
    const ch = chars[idx]
    recurse(idx + 1, current)
    for (const [from, to] of CONFUSION_PAIRS) {
      if (ch === from) {
        const next = [...current]
        next[idx] = to
        variants.add(next.join(''))
        recurse(idx + 1, next)
      }
    }
  }

  recurse(0, chars)
  return Array.from(variants)
}

function collectRawStrings(ocrInput) {
  if (typeof ocrInput === 'string') return [fixOcrText(ocrInput)]
  if (!Array.isArray(ocrInput)) return []

  return ocrInput
    .map((item) => {
      if (typeof item === 'string') return fixOcrText(item)
      if (item?.text) return fixOcrText(item.text)
      return ''
    })
    .filter(Boolean)
}

function fragmentsFromLines(lines) {
  const fragments = []
  for (const line of lines) {
    const text = fixOcrText(line?.text ?? line)
    if (!text) continue
    const score = Number(line?.score ?? line?.confidence ?? 0.75)
    fragments.push({ text, score })
    text.split(/\s+/).forEach((part) => {
      if (part.length >= 2) fragments.push({ text: part, score: score * 0.95 })
    })
  }
  return fragments
}

function buildCandidatesFromText(texts) {
  const candidates = new Map()

  const add = (raw, ocrScore = 0.7) => {
    const base = normalizeLicensePlate(raw)
    if (base.length < 4 || base.length > 12) return

    const variants = generateConfusionVariants(base)
    for (const norm of variants) {
      if (norm.length < 4 || norm.length > 12 || !/[0-9]/.test(norm)) continue
      const pat = patternScore(norm)
      const combined = Math.min(
        99,
        Math.round(pat * 0.75 + ocrScore * 25 + Math.min(norm.length, 8))
      )
      const prev = candidates.get(norm)
      if (!prev || combined > prev.confidence) {
        candidates.set(norm, { registration: norm, confidence: combined, patternScore: pat })
      }
    }
  }

  for (const t of texts) {
    add(t, 0.75)
    const compact = t.replace(/\s+/g, '')
    if (compact !== t) add(compact, 0.88)
    const runs = t.match(/[A-Z0-9][A-Z0-9\s\-]{2,14}[A-Z0-9]/gi) || []
    runs.forEach((run) => add(run, 0.8))
    const alnumRuns = t.match(/[A-Z0-9]{4,12}/g) || []
    alnumRuns.forEach((run) => add(run, 0.86))
    const tokens = t.split(/[^A-Z0-9]+/).filter(Boolean)
    tokens.forEach((tok) => add(tok, 0.78))
    for (let i = 0; i < tokens.length - 1; i += 1) {
      add(tokens[i] + tokens[i + 1], 0.82)
      if (i + 2 < tokens.length) add(tokens[i] + tokens[i + 1] + tokens[i + 2], 0.85)
    }
  }

  return Array.from(candidates.values()).sort((a, b) => b.confidence - a.confidence)
}

/**
 * Extract the best SA registration from OCR fragments.
 * @param {string|string[]|Array<{text:string,score?:number}>} ocrInput
 * @param {{ minConfidence?: number }} options
 */
export function extractRegistration(ocrInput, options = {}) {
  const minConfidence = options.minConfidence ?? 28
  const lines = Array.isArray(ocrInput) ? fragmentsFromLines(ocrInput) : []
  const strings = collectRawStrings(ocrInput)
  lines.forEach(({ text, score }) => strings.push(text))

  const uniqueStrings = Array.from(new Set(strings))
  const candidates = buildCandidatesFromText(uniqueStrings)

  const best = candidates[0] || null
  if (!best || best.confidence < minConfidence) {
    return {
      registration: null,
      confidence: best?.confidence ?? 0,
      candidates: candidates.slice(0, 5),
      rawText: uniqueStrings.join(' | '),
    }
  }

  return {
    registration: best.registration,
    confidence: best.confidence,
    candidates: candidates.slice(0, 5),
    rawText: uniqueStrings.join(' | '),
  }
}

export { PROVINCES, PLATE_PATTERNS }
